import { TASK_TYPES } from 'features/daily-plan/constants';
import {
  ALLOWED_TASKS_BY_CONTACT_ROLE,
  CONTACT_DRAWERS,
  CONTACT_PROFILE_CONFIG
} from 'features/inventory-profiles/constants';
import { buildOptOutDetail, formatLocation } from 'features/inventory-profiles/utils';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';

const COPIES = strings.inventoryProfiles;
const DATA_SHEET_COPIES = COPIES.sections.tabs.dataSheet;

/**
 * Maps the Contact API response to the layout props.
 * @param {Object} contact Contact profile
 * @returns {{ header: Object, summary: Object, status: Object }}
 */
export const formatContactResponse = contact => ({
  header: {
    entityType: CONTACT_PROFILE_CONFIG.type,
    title: contact.personalInformation?.full_name,
    lastActivity: contact.last_activity_date
  },
  summary: {
    company: { id: contact.company_id, name: contact.current_company },
    location: formatLocation(contact.personalInformation?.address?.city),
    created: {
      date: contact.created_at,
      by: contact.createdBy?.personalInformation?.full_name
    },
    details: [buildOptOutDetail(contact)]
  },
  status: {
    inventoryType: contact.hiringAuthorityStatus,
    itemStatus: contact.status,
    subStatus: contact.subStatus,
    optOutStatus: contact.optOutStatus,
    emailValidationStatus: contact.emailValidation?.verdict?.name,
    otherEmailValidationStatus: contact.personalEmailValidation?.verdict?.name,
    entityTypeId: CONTACT_PROFILE_CONFIG.id
  }
});

/**
 * Route of the profile a converted contact became: Candidate when present, else Hiring Authority.
 * @param {Object} conversion Results of the NameConvertedStatus endpoint
 * @returns {string}
 */
export const getConvertedProfileRoute = conversion =>
  conversion.candidate
    ? EntityRoutes.CandidateProfile.replace(':id', conversion.candidate.id)
    : EntityRoutes.HiringAuthorityProfile.replace(':id', conversion.hiringAuthority.id);

/**
 * Contact ⋮ menu items, in V1 order.
 * @param {Object} params
 * @param {Object} params.permissions Result of useContactPermissions
 * @param {Array} params.searchProjectMenuItems `SearchProjectsMenuItems` of useSearchProjectInProfiles (V1)
 * @param {Array} params.restrictionMenuItems `menuItems` of useProfileRestrictions
 * @param {(name: string) => () => void} params.openDrawer From useProfileDrawer
 * @param {() => void} params.onAddCandidate `addCandidate` of useContactNavigation
 * @returns {Array<{ title: string, action: () => void, visible: boolean }>}
 */
export const buildContactMenuItems = ({
  onAddCandidate,
  openDrawer,
  permissions,
  restrictionMenuItems,
  searchProjectMenuItems
}) => [
  {
    title: COPIES.common.editProfile,
    action: openDrawer(CONTACT_DRAWERS.Edit),
    visible: permissions.canEdit
  },
  ...searchProjectMenuItems,
  {
    title: COPIES.common.candidateAddition,
    action: onAddCandidate,
    visible: permissions.canAddCandidate
  },
  {
    title: COPIES.menu.addAction,
    action: openDrawer(CONTACT_DRAWERS.Task),
    visible: permissions.canUseDailyPlan
  },
  ...restrictionMenuItems
];

/**
 * Task types a contact can get, by its role. Shared by the Action Plan tab and the Task drawer.
 * @param {number} [roleId] `contact.status?.id`
 * @returns {string[]}
 */
export const getContactAllowedTaskTypes = roleId =>
  ALLOWED_TASKS_BY_CONTACT_ROLE[roleId] ?? [TASK_TYPES.other];

/**
 * Data sheet period: `from - to`, `from - Present` without an end, or the one date present.
 * @param {{ from?: string, to?: string }} [fromTo]
 * @returns {string|null}
 */
const buildPeriod = fromTo => {
  if (!fromTo) return null;

  const { from, to } = fromTo;
  if (!from && !to) return null;

  if (!from) return to;
  const toStr = to || DATA_SHEET_COPIES.period.present;
  return `${from} - ${toStr}`;
};

/**
 * Adds the period, title, subtitle and description the Data Sheet tab shows to each item.
 * @param {Object} data Contact data sheet response
 * @returns {Object}
 */
export const formatDatasheetResponse = data => ({
  ...data,
  candidateBackground: {
    ...data?.candidateBackground,
    educationItems: data?.candidateBackground?.educationItems?.map(education => ({
      ...education,
      period: buildPeriod(education?.fromTo),
      title:
        education?.degreeMajor?.degree || education?.degreeMajor?.major
          ? strings.formatString(DATA_SHEET_COPIES.sections.education.degreeAndMajor, {
              degree: education?.degreeMajor?.degree || '',
              major: education?.degreeMajor?.major || ''
            })
          : null,
      subtitle: education?.school || null,
      description: education?.description || null
    })),
    employmentHistoryItems: data?.candidateBackground?.employmentHistoryItems?.map(experience => ({
      ...experience,
      period: buildPeriod(experience?.fromTo),
      title: experience?.companyTitle?.title || null,
      subtitle: experience?.companyTitle?.company || null,
      description:
        experience?.dutiesAndResponsibilities || experience?.accomplishments
          ? strings.formatString(DATA_SHEET_COPIES.sections.experience.description, {
              dutiesAndResponsibilities: experience.dutiesAndResponsibilities || '',
              accomplishments: experience.accomplishments || ''
            })
          : null
    })),
    licensesAndCertificationsItems: data?.candidateBackground?.licensesAndCertificationsItems?.map(
      item => ({
        ...item,
        title: item?.name || null,
        subtitle: item?.issuingOrganization || null,
        period: buildPeriod({
          from: item?.issueAndExpirationDate?.issueDate,
          to: item?.issueAndExpirationDate?.expirationDate
        })
      })
    )
  },
  professionalInsights: {
    ...data?.professionalInsights,
    languages: data?.professionalInsights?.languages?.map(language => ({
      ...language,
      name: language?.title || language?.language || null
    }))
  }
});
